<template>
  <div class="assessment-numeric-input" @focusout="onFocusOut">
    <div
      class="assessment-numeric-input__value"
      :class="{ 'assessment-numeric-input__value--unknown': isUnknown }"
      data-testid="assessment-numeric-value"
    >
      <input
        :id="`${inputId}-number`"
        :value="numericAnswer === null ? '' : numericAnswer"
        type="number"
        inputmode="decimal"
        data-testid="assessment-numeric-direct-input"
        :aria-label="label"
        :placeholder="isUnknown ? unknownDisplay : ''"
        :min="minimum"
        :max="maximum"
        :step="step"
        :disabled="disabled"
        @input="onDirectInput"
        @keydown.enter="completeEntry"
      >
      <span v-if="!isUnknown" class="assessment-numeric-input__unit">{{ unit }}</span>
    </div>
    <label :for="inputId" class="assessment-numeric-input__label">{{ label }}</label>
    <div class="assessment-numeric-input__slider">
      <button
        type="button"
        class="assessment-numeric-input__step"
        data-testid="assessment-numeric-decrease"
        :aria-label="decreaseLabel"
        :disabled="disabled || !canDecrease"
        @click="adjustValue(-1)"
      >
        −
      </button>
      <input
        :id="inputId"
        :value="numericValue"
        type="range"
        data-testid="assessment-numeric-range"
        :min="minimum"
        :max="maximum"
        :step="step"
        :disabled="disabled"
        @input="onRangeInput"
      >
      <button
        type="button"
        class="assessment-numeric-input__step"
        data-testid="assessment-numeric-increase"
        :aria-label="increaseLabel"
        :disabled="disabled || !canIncrease"
        @click="adjustValue(1)"
      >
        +
      </button>
    </div>
    <div class="assessment-numeric-input__range">
      <span>{{ minimum }} {{ unit }}</span><span>{{ maximum }} {{ unit }}</span>
    </div>
    <button
      v-if="allowUnknown"
      type="button"
      class="assessment-numeric-input__unknown"
      data-testid="assessment-numeric-unknown"
      :class="{ 'assessment-numeric-input__unknown--selected': isUnknown }"
      :aria-pressed="String(isUnknown)"
      :disabled="disabled"
      @click="selectUnknown"
    >
      {{ unknownLabel }}
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import './styles/NumericSliderInput.css'

const OUTSIDE_TAP_TIMEOUT_MS = 500

export default defineComponent({
  name: 'AssessmentNumericSliderInput',
  props: {
    value: { type: [String, Number] as PropType<string | number | null>, default: null },
    minimum: { type: Number, required: true },
    maximum: { type: Number, required: true },
    step: { type: Number, default: 1 },
    unit: { type: String, default: '' },
    label: { type: String, required: true },
    inputId: { type: String, required: true },
    allowUnknown: { type: Boolean, default: false },
    unknownValue: { type: String, default: 'unknown' },
    unknownLabel: { type: String, default: "I don't know" },
    unknownDisplay: { type: String, default: "I don't know" },
    decreaseLabel: { type: String, default: 'Decrease value' },
    increaseLabel: { type: String, default: 'Increase value' },
    disabled: { type: Boolean, default: false },
  },
  emits: ['input', 'entry-complete'],
  data: () => ({ pendingEntry: false, outsideTapTimer: null as ReturnType<typeof setTimeout> | null }),
  mounted(): void {
    document.addEventListener('pointerdown', this.onOutsidePointerDown)
  },
  beforeDestroy(): void {
    document.removeEventListener('pointerdown', this.onOutsidePointerDown)
    this.cancelOutsideTap()
  },
  computed: {
    numericAnswer(): number | null {
      return typeof this.value === 'number' && Number.isFinite(this.value) ? this.value : null
    },
    numericValue(): number {
      return this.numericAnswer ?? this.minimum
    },
    isUnknown(): boolean {
      return typeof this.value === 'string'
        && this.value.toLowerCase() === this.unknownValue.toLowerCase()
    },
    canDecrease(): boolean {
      return this.numericValue > this.minimum
    },
    canIncrease(): boolean {
      return this.numericValue < this.maximum
    },
  },
  methods: {
    completeEntry(): void {
      this.cancelOutsideTap()
      this.pendingEntry = false
      this.$emit('entry-complete')
    },
    cancelOutsideTap(): void {
      document.removeEventListener('click', this.finishOutsideTap, true)
      if (this.outsideTapTimer !== null) clearTimeout(this.outsideTapTimer)
      this.outsideTapTimer = null
    },
    finishOutsideTap(): void {
      const pending = this.pendingEntry
      this.cancelOutsideTap()
      if (pending) this.completeEntry()
    },
    onFocusOut(event: FocusEvent): void {
      const next = event.relatedTarget as Node | null
      if ((next && this.$el.contains(next)) || this.outsideTapTimer !== null) return
      this.completeEntry()
    },
    onOutsidePointerDown(event: Event): void {
      if (!this.pendingEntry || this.outsideTapTimer !== null || this.$el.contains(event.target as Node)) return
      document.addEventListener('click', this.finishOutsideTap, true)
      this.outsideTapTimer = setTimeout(this.finishOutsideTap, OUTSIDE_TAP_TIMEOUT_MS)
    },
    onRangeInput(event: Event): void {
      this.pendingEntry = true
      this.$emit('input', Number((event.target as HTMLInputElement).value))
    },
    onDirectInput(event: Event): void {
      this.pendingEntry = true
      const input = event.target as HTMLInputElement
      if (input.value === '') {
        this.$emit('input', null)
        return
      }

      const rawValue = Number(input.value)
      if (Number.isFinite(rawValue)) this.$emit('input', rawValue)
    },
    adjustValue(direction: number): void {
      const nextValue = this.numericValue + (direction * this.step)
      const boundedValue = Math.min(this.maximum, Math.max(this.minimum, nextValue))
      this.pendingEntry = true
      this.$emit('input', Number(boundedValue.toFixed(10)))
    },
    selectUnknown(): void {
      this.$emit('input', this.unknownValue)
      this.completeEntry()
    },
  },
})
</script>
